import React, {createContext, useCallback, useContext, useRef, useState} from 'react';
import {useGoogleLogin, googleLogout} from '@react-oauth/google';
import {useQueryClient} from '@tanstack/react-query';
import {clearJsonFiles} from '../drive/jsonFileStore';

export interface UserProfile {
    email: string;
    name: string;
    picture: string;
}

interface StoredAuth {
    accessToken: string;
    expiresAt: number;
    profile: UserProfile;
}

export interface AuthContextValue {
    accessToken: string | null;
    profile: UserProfile | null;
    isSignedIn: boolean;
    isAuthenticating: boolean;
    /** The token expired and couldn't be renewed without a click — `reauthenticate` must be called from one. */
    needsReauth: boolean;
    error: string | null;
    login: () => void;
    /** Renews the token for the current account; must be called from a click so the GIS popup isn't blocked. */
    reauthenticate: () => void;
    logout: () => void;
    /** Resolves to a currently-valid access token, silently refreshing it first if it's near expiry (or `force` is set). */
    getValidAccessToken: (force?: boolean) => Promise<string>;
}

/** Thrown by `getValidAccessToken` when renewing the token needs the user to click "Continue". */
export class ReauthRequiredError extends Error {
    constructor() {
        super('A munkameneted lejárt. Kattints a Folytatás gombra.');
        this.name = 'ReauthRequiredError';
    }
}

// Full drive scope is required: we read/create/move/delete files that were not necessarily
// created by this app, so the restricted `drive.file` scope isn't sufficient.
const SCOPES = [
    'https://www.googleapis.com/auth/drive',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/userinfo.profile',
].join(' ');

const STORAGE_KEY = 'spot-drive-auth';
// Kept across tabs/reloads so GIS can skip the account chooser; cleared on explicit logout.
const LAST_EMAIL_KEY = 'spot-drive-last-email';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadStoredAuth(): StoredAuth | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredAuth;
        if (!parsed.accessToken || !parsed.expiresAt || !parsed.profile) return null;
        return parsed;
    } catch {
        return null;
    }
}

function loadLastEmail(): string | undefined {
    try {
        return localStorage.getItem(LAST_EMAIL_KEY) ?? undefined;
    } catch {
        return undefined;
    }
}

function saveLastEmail(email: string | null) {
    try {
        if (email) {
            localStorage.setItem(LAST_EMAIL_KEY, email);
        } else {
            localStorage.removeItem(LAST_EMAIL_KEY);
        }
    } catch {
        // Storage unavailable (private mode etc.) — we just lose the hint.
    }
}

/**
 * GIS renews tokens through a popup, which browsers block unless it's opened right after a click.
 * Browsers without the User Activation API report `undefined`, in which case we just try.
 */
function hasUserActivation(): boolean {
    const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
    return activation ? activation.isActive : true;
}

async function fetchProfile(accessToken: string): Promise<UserProfile> {
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {Authorization: `Bearer ${accessToken}`},
    });
    if (!response.ok) {
        throw new Error('A felhasználói profil lekérése sikertelen.');
    }
    const data = await response.json();
    return {email: data.email, name: data.name, picture: data.picture};
}

export function AuthProvider({children}: { children: React.ReactNode }) {
    const queryClient = useQueryClient();
    const [auth, setAuth] = useState<StoredAuth | null>(() => loadStoredAuth());
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [needsReauth, setNeedsReauth] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The token logic reads these mirrors instead of the state above: React Query re-runs query
    // functions captured in earlier renders (a refetch reuses the last run's queryFn), whose
    // render-time `auth` can still be the expired token right after a successful re-login.
    const authRef = useRef(auth);
    const needsReauthRef = useRef(false);

    // Resolvers/rejecters for whoever is awaiting the in-flight login/refresh attempt.
    const pendingResolvers = useRef<Array<(token: string) => void>>([]);
    const pendingRejecters = useRef<Array<(err: Error) => void>>([]);
    // Whether the in-flight GIS request renews an existing session (vs. a fresh sign-in), and
    // whether one is in flight at all — so concurrent callers share a single popup.
    const requestKind = useRef<'login' | 'refresh' | null>(null);

    const persist = useCallback((next: StoredAuth | null) => {
        authRef.current = next;
        setAuth(next);
        if (next) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const updateNeedsReauth = useCallback((value: boolean) => {
        needsReauthRef.current = value;
        setNeedsReauth(value);
    }, []);

    const settlePending = useCallback((token: string | null, err: Error | null) => {
        const resolvers = pendingResolvers.current;
        const rejecters = pendingRejecters.current;
        pendingResolvers.current = [];
        pendingRejecters.current = [];
        requestKind.current = null;
        if (token) {
            resolvers.forEach((resolve) => resolve(token));
        } else if (err) {
            rejecters.forEach((reject) => reject(err));
        }
    }, []);

    /** A failed renewal keeps the user signed in and asks for a click instead of dropping to the sign-in screen. */
    const failRequest = useCallback((message: string) => {
        if (requestKind.current === 'refresh') {
            updateNeedsReauth(true);
            settlePending(null, new ReauthRequiredError());
        } else {
            setError(message);
            settlePending(null, new Error(message));
        }
        setIsAuthenticating(false);
    }, [settlePending, updateNeedsReauth]);

    const handleTokenResponse = useCallback(async (tokenResponse: { access_token: string; expires_in: number }) => {
        const wasRefresh = requestKind.current === 'refresh';
        try {
            const profile = await fetchProfile(tokenResponse.access_token);
            const next: StoredAuth = {
                accessToken: tokenResponse.access_token,
                expiresAt: Date.now() + tokenResponse.expires_in * 1000,
                profile,
            };
            persist(next);
            saveLastEmail(profile.email);
            setError(null);
            updateNeedsReauth(false);
            settlePending(next.accessToken, null);
            if (wasRefresh) {
                // Queries that failed while the session was expired won't retry on their own.
                queryClient.refetchQueries({predicate: (query) => query.state.status === 'error'});
            }
        } catch (e) {
            const err = e instanceof Error ? e : new Error('A bejelentkezés sikertelen.');
            setError(err.message);
            settlePending(null, err);
        } finally {
            setIsAuthenticating(false);
        }
    }, [persist, settlePending, updateNeedsReauth, queryClient]);

    const login = useGoogleLogin({
        flow: 'implicit',
        scope: SCOPES,
        onSuccess: handleTokenResponse,
        onError: (errorResponse) => {
            failRequest(errorResponse.error_description ?? 'A bejelentkezés sikertelen.');
        },
        onNonOAuthError: () => {
            failRequest('A bejelentkezést megszakították vagy letiltották (ellenőrizd a felugróablakok tiltását).');
        },
    });

    const startRequest = useCallback((kind: 'login' | 'refresh', hint: string | undefined) => {
        requestKind.current = kind;
        setIsAuthenticating(true);
        // With a hint, `prompt: ''` lets GIS pick the account and close the popup by itself;
        // without one (first sign-in, or after logout) the user has to choose.
        login(hint ? {prompt: '', hint} : {prompt: 'select_account'});
    }, [login]);

    const triggerInteractiveLogin = useCallback(() => {
        if (requestKind.current) return;
        setError(null);
        startRequest('login', loadLastEmail());
    }, [startRequest]);

    const reauthenticate = useCallback(() => {
        if (requestKind.current) return;
        setError(null);
        startRequest('refresh', authRef.current?.profile.email ?? loadLastEmail());
    }, [startRequest]);

    const logout = useCallback(() => {
        googleLogout();
        saveLastEmail(null);
        clearJsonFiles();
        updateNeedsReauth(false);
        persist(null);
    }, [persist, updateNeedsReauth]);

    const getValidAccessToken = useCallback((force = false): Promise<string> => {
        const current = authRef.current;
        if (!force && !needsReauthRef.current && current && current.expiresAt - Date.now() > 60_000) {
            return Promise.resolve(current.accessToken);
        }
        return new Promise<string>((resolve, reject) => {
            pendingResolvers.current.push(resolve);
            pendingRejecters.current.push(reject);
            if (requestKind.current) return; // piggyback on the popup that's already open
            if (!hasUserActivation()) {
                // e.g. a refetch on tab focus: the popup would be blocked, so ask for a click instead.
                updateNeedsReauth(true);
                settlePending(null, new ReauthRequiredError());
                return;
            }
            startRequest('refresh', current?.profile.email ?? loadLastEmail());
        });
    }, [settlePending, startRequest, updateNeedsReauth]);

    const value: AuthContextValue = {
        accessToken: auth?.accessToken ?? null,
        profile: auth?.profile ?? null,
        isSignedIn: !!auth,
        isAuthenticating,
        needsReauth,
        error,
        login: triggerInteractiveLogin,
        reauthenticate,
        logout,
        getValidAccessToken,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return ctx;
}
